import { FC, ReactElement, useEffect, useId } from 'react';

import { Loader } from '@pp/core/src/components/loader/Loader';
import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { GameStatus } from '@pp/core/src/store/slices/game/types';

import { HIGHFLYER_PT } from '../../../../config/tableConfigParserOptions';
import { useTableTopDataFetcher } from '../../../../store/apiHooks/useTableTopDataFetcher';
import { useGameDispatch } from '../../../../store/GameStore';
import { useHighflyerGameStateSlice } from '../../../../store/slices/highflyerGame/selectors';
import { highflyerGameStateActions } from '../../../../store/slices/highflyerGame/slice';
import { ScrollableWrapper } from '../../../tableSheet/scrollableWrapper/ScrollableWrapper';
import { ErrorMessage } from '../../errorMessage/ErrorMessage';
import { TableTopInfo, TableTopTab } from '../type';

import { CashedMultipliersAndWinBottomBar } from './singleCashedMultipliersAndWins/CashedMultipliersAndWinBottomBar';
import { CashedMultipliersAndWinTopBar } from './singleCashedMultipliersAndWins/CashedMultipliersAndWinTopBar';

import styles from './CashedMultipliersAndWins.module.css';

export const TableTopCashedMultipliers: FC<{ showStaticData?: boolean }> = ({ showStaticData = false }) => {
  const id = useId();
  const dispatch = useGameDispatch();
  const tableTopDataFetcher = useTableTopDataFetcher();
  const isApiRejected = useHighflyerGameStateSlice.getIsApiRejected();
  const tableTopData = useHighflyerGameStateSlice.tableTopData().cashouts;
  const currentPeriod = useHighflyerGameStateSlice.currentCalenderOption();
  const isTimerStarted = useGameStateSlice.getCurrentStatus() === GameStatus.PlaceYourBets;
  const highFlyerPT = useConfigSlice.getTableVariant() === HIGHFLYER_PT;

  const dummyData = [
    {
      userId: 'ppc1720045158050',
      nickname: 'Nova',
      betAmount: 10,
      cashOutMultiplier: highFlyerPT ? 60 : 99.99,
      winAmount: highFlyerPT ? 600 : 999.9,
      betTime: '2025-08-29T02:48:19.000Z',
      multiplier: highFlyerPT ? 99 : 99.99,
      gameEndTime: '2025-08-29T02:49:47.000Z',
      gameId: '9625316513',
      tableId: 'haphflyer2201jfm',
    },
    {
      userId: 'ppc1735195299422',
      nickname: 'DuckTales',
      betAmount: 10,
      cashOutMultiplier: highFlyerPT ? 55 : 87.91,
      winAmount: highFlyerPT ? 550 : 879.1,
      betTime: '2025-08-29T02:48:15.000Z',
      multiplier: highFlyerPT ? 98 : 99.99,
      gameEndTime: '2025-08-29T02:49:47.000Z',
      gameId: '9625316513',
      tableId: 'haphflyer2201jfm',
    },
  ];
  const _tableTopData = showStaticData ? dummyData : tableTopData;

  useEffect(() => {
    dispatch(highflyerGameStateActions.setErrorOccured());
    dispatch(highflyerGameStateActions.setCurrentTopTab(TableTopTab.CASHED_MULTIPLIERS));
  }, [dispatch]);

  useEffect(() => {
    dispatch(highflyerGameStateActions.setErrorOccured());
    tableTopDataFetcher(TableTopTab.CASHED_MULTIPLIERS, true);
  }, [currentPeriod, dispatch, tableTopDataFetcher]);

  useEffect(() => {
    if (!isApiRejected && isTimerStarted) {
      tableTopDataFetcher(TableTopTab.CASHED_MULTIPLIERS, isTimerStarted);
    }
  }, [isApiRejected, isTimerStarted, tableTopDataFetcher]);

  return (
    <ScrollableWrapper>
      {((): ReactElement => {
        if (!_tableTopData && isApiRejected) {
          return (
            <ErrorMessage
              title="HIGHFLYER_FAILED_TO_LOAD"
              message="HIGHFLYER_INTERNAL_ERROR"
              disabled={false}
              loading={false}
              onClick={(): void => {
                tableTopDataFetcher(TableTopTab.CASHED_MULTIPLIERS, true);
                dispatch(highflyerGameStateActions.setErrorOccured());
              }}
            />
          );
        } else if (!_tableTopData && !isApiRejected) {
          return <Loader />;
        } else {
          return (
            <div className={styles.tableTop_wins_container}>
              {_tableTopData &&
                _tableTopData.map((item: TableTopInfo, index: number) => {
                  return (
                    <div className={styles.singleItem} key={`table-top-cashed-${id}`}>
                      <CashedMultipliersAndWinTopBar
                        betAmount={item.betAmount}
                        winAmount={item.winAmount}
                        multiplier={item.cashOutMultiplier}
                        showStaticData={showStaticData}
                      />
                      <CashedMultipliersAndWinBottomBar
                        nickname={item.nickname}
                        betTime={item.betTime}
                        crashMul={item.multiplier}
                        index={index}
                        showStaticData={showStaticData}
                      />
                    </div>
                  );
                })}
            </div>
          );
        }
      })()}
    </ScrollableWrapper>
  );
};
